import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ShieldCheck } from 'lucide-react';
import { useConversionTracking } from '@/hooks/useMarketingTracking';

function setCookie(name: string, value: string, days: number = 365) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export default function NewsletterPopup() {
  const location = useLocation();
  const { trackNewsletterSignup } = useConversionTracking();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const dismissed = getCookie('newsletter-popup-dismissed') || localStorage.getItem('newsletter-popup-dismissed');
    if (dismissed === 'true') return;

    const handleScroll = () => {
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercentage > 30) {
        setIsVisible(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    const timeoutId = setTimeout(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const handleClose = () => {
    setIsVisible(false);
    setCookie('newsletter-popup-dismissed', 'true', 365);
    localStorage.setItem('newsletter-popup-dismissed', 'true');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'Newsletter Popup' }),
      });
      if (!response.ok) throw new Error('Failed');
      trackNewsletterSignup('popup');
      setSubmitStatus('success');
      setTimeout(handleClose, 2000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (location.pathname !== '/') return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-brand-black/80 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            className="relative bg-white max-w-lg w-full overflow-hidden rounded-sm shadow-2xl"
          >
            <button
              onClick={handleClose}
              className="absolute top-6 right-6 z-10 text-brand-black/20 hover:text-brand-black transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="hidden md:block md:col-span-4 bg-brand-black relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                  <img src="/images/delta-logo-white.svg" alt="" className="w-full h-full object-center p-8 rotate-45" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <span className="text-brand-blue font-display font-bold text-4xl rotate-90 whitespace-nowrap tracking-tighter uppercase">Community</span>
                </div>
              </div>

              <div className="md:col-span-8 p-10 md:p-14">
                <span className="text-brand-blue font-display text-[10px] tracking-[0.4em] uppercase mb-6 block font-bold">Exclusive Offer</span>
                <h2 className="text-4xl font-display font-bold text-brand-black mb-6 tracking-tighter leading-none">
                  JOIN THE <br />
                  <span className="text-gray-200 uppercase">Family.</span>
                </h2>
                
                {submitStatus === 'success' ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-10 text-center">
                    <ShieldCheck className="w-12 h-12 text-brand-blue mx-auto mb-4" />
                    <p className="font-bold tracking-widest uppercase text-xs">Welcome to the family.</p>
                  </motion.div>
                ) : (
                  <>
                    <p className="text-gray-500 text-sm leading-relaxed mb-10 font-light">
                      Subscribe to our community updates and receive <span className="text-brand-black font-bold">2 free introductory classes</span> at Delta BJJ.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="relative">
                        <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email Address" required
                          className="w-full bg-transparent border-b border-gray-200 pl-8 py-4 focus:border-brand-blue outline-none transition-colors text-brand-black font-light text-sm"
                        />
                      </div>
                      <button
                        type="submit" disabled={isSubmitting}
                        className="btn-premium-blue w-full text-center mt-6"
                      >
                        {isSubmitting ? 'Processing...' : 'Claim Access'}
                      </button>
                      <button
                        type="button" onClick={handleClose}
                        className="w-full text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-4 hover:text-brand-black transition-colors"
                      >
                        Maybe Later
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
